import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Heart, MessageCircle, Sparkles, Trash2, Loader2, Plus,
  ChevronDown, ChevronUp, Send, X, Reply, Lightbulb, ImageIcon, Type, Flag, Pencil
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL as API, BACKEND_URL } from '@/utils/apiConfig';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { MessageDisplay } from '@/components/MessageDisplay';

// ── Reaction Emoji Map ──────────────────────────────────────────────────────

const SKIN_TONES = [
  { key: 'default', label: 'Default', modifier: '' },
  { key: 'light', label: 'Light', modifier: '\u{1F3FB}' },
  { key: 'medium-light', label: 'Medium Light', modifier: '\u{1F3FC}' },
  { key: 'medium', label: 'Medium', modifier: '\u{1F3FD}' },
  { key: 'medium-dark', label: 'Medium Dark', modifier: '\u{1F3FE}' },
  { key: 'dark', label: 'Dark', modifier: '\u{1F3FF}' },
];

const REACTIONS = [
  { type: 'heart', base: '\u2764\uFE0F', hasSkin: false },
  { type: 'pray', base: '\u{1F64F}', hasSkin: true },
  { type: 'idea', base: '\u{1F4A1}', hasSkin: false },
  { type: 'clap', base: '\u{1F44F}', hasSkin: true },
  { type: 'thumbsdown', base: '\u{1F44E}', hasSkin: true },
];

function getReactionEmoji(type, skinTone = 'default') {
  const r = REACTIONS.find((x) => x.type === type);
  if (!r) return '?';
  if (!r.hasSkin || skinTone === 'default') return r.base;
  const tone = SKIN_TONES.find((s) => s.key === skinTone);
  // For skin-toned emoji, we replace the base with base + modifier
  // The base emoji for pray is \u{1F64F}, for clap is \u{1F44F}
  return String.fromCodePoint(r.base.codePointAt(0)) + (tone?.modifier || '');
}

// ── Sub-Tab Toggle ──────────────────────────────────────────────────────────

function SubTab({ active, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap ${
        active
          ? 'text-white'
          : 'text-[var(--theme-textSecondary)] hover:text-[var(--theme-text)]'
      }`}
      style={active ? { background: 'var(--theme-accent)' } : { background: 'var(--glass-bg)' }}
    >
      {label}
    </button>
  );
}

// ── Eili Response Popup Modal ───────────────────────────────────────────────

function EiliResponseModal({ post, open, onClose, onDiscuss }) {
  const { t } = useTranslation();
  const [discussing, setDiscussing] = useState(false);

  if (!open || !post?.eili_response) return null;

  const handleDiscuss = async () => {
    setDiscussing(true);
    await onDiscuss(post.post_id);
    setDiscussing(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={`eili-modal-${post.post_id}`}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />
      <div
        className="relative w-full max-w-lg overflow-y-auto rounded-2xl p-5"
        style={{
          background: 'var(--theme-surface)',
          border: '1px solid var(--glass-border)',
          maxHeight: 'min(85vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--theme-accent)' }}>{t('feed.shared_response')}</span>
            {post.eili_response.conversation_title && (
              <span className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('generated.communityfeed_from_0be9')}{post.eili_response.conversation_title}"
                              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[var(--theme-hover)]" data-testid="close-eili-modal">
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        {/* Question */}
        {post.eili_response.question && (
          <div className="rounded-xl p-3 mb-3" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
            <p className="text-xs font-medium mb-0.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.communityfeed_question_fb6e')}</p>
            <p className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{post.eili_response.question}</p>
          </div>
        )}

        {/* Full Response with collapsible sections */}
        <div className="text-sm leading-relaxed" style={{ color: 'var(--theme-text)' }}>
          <MessageDisplay content={post.eili_response.content} />
        </div>

        {/* Ask Eili button */}
        <div className="mt-4 pt-3 flex justify-end" style={{ borderTop: '1px solid var(--glass-border)' }}>
          <Button
            onClick={handleDiscuss}
            disabled={discussing}
            size="sm"
            className="rounded-full px-4"
            style={{ background: 'var(--theme-accent)', color: '#fff' }}
            data-testid={`modal-ask-eili-${post.post_id}`}
          >
            {discussing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            {t('generated.communityfeed_ask_eili_about_af19')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Report Modal ────────────────────────────────────────────────────────────

const REPORT_REASONS = [
  'feed.report_reasons.harassment',
  'feed.report_reasons.inappropriate',
  'feed.report_reasons.spam',
  'feed.report_reasons.misinformation',
  'feed.report_reasons.other',
];

function ReportModal({ open, onClose, targetType, targetId }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!reason) { toast.error(t('generated.communityfeed_please_select_a_2167')); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/community/report`, {
        target_type: targetType,
        target_id: targetId,
        reason,
        details: details.trim() || undefined,
      }, { withCredentials: true });
      toast.success(t('feed.report_submitted'));
      onClose();
      setReason('');
      setDetails('');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : t('feed.failed_report'));
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="report-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-md rounded-t-2xl sm:rounded-2xl p-5"
        style={{ background: 'var(--theme-surface)', border: '1px solid var(--glass-border)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Flag className="h-4 w-4" style={{ color: '#ef4444' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>
              {t('feed.report_post')}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[var(--theme-hover)]" data-testid="close-report-modal">
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        <p className="text-xs mb-3" style={{ color: 'var(--theme-textSecondary)' }}>
          {t('feed.report_post')}?
        </p>

        <div className="space-y-1.5 mb-4">
          {REPORT_REASONS.map((r) => (
            <button
              key={r}
              onClick={() => setReason(r)}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-all ${
                reason === r ? 'ring-1' : ''
              }`}
              style={{
                background: reason === r ? 'var(--glass-bg-active)' : 'var(--glass-bg)',
                color: 'var(--theme-text)',
                ringColor: 'var(--theme-accent)',
                border: '1px solid var(--glass-border)',
              }}
              data-testid={`report-reason-${r.toLowerCase().replace(/\s/g, '-')}`}
            >
              {t(r)}
            </button>
          ))}
        </div>

        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder={t('feed.additional_details')}
          rows={2}
          className="w-full rounded-xl px-3 py-2 text-sm mb-4 resize-none focus:outline-none"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
          data-testid="report-details-input"
        />

        <Button
          onClick={handleSubmit}
          disabled={submitting || !reason}
          className="w-full rounded-xl"
          style={{ background: '#ef4444', color: '#fff' }}
          data-testid="submit-report-btn"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Flag className="h-4 w-4 mr-2" />}
          {t('generated.communityfeed_submit_report_184e')}
        </Button>
      </div>
    </div>
  );
}

// ── Reaction Bar ────────────────────────────────────────────────────────────

function ReactionBar({ postId, reactions, userReaction, onReactionChange }) {
  const { t } = useTranslation();
  const [showPicker, setShowPicker] = useState(null); // 'pray' | 'clap' | null for skin tone picker
  const [busy, setBusy] = useState(false);

  const handleReact = async (type, skinTone = 'default') => {
    setBusy(true);
    setShowPicker(null);
    try {
      // If already reacted with this type, remove it (toggle off)
      if (userReaction && userReaction.type === type) {
        await axios.delete(`${API}/feed/posts/${postId}/react`, { withCredentials: true });
        onReactionChange(null);
      } else {
        const res = await axios.post(`${API}/feed/posts/${postId}/react`, { type, skin_tone: skinTone }, { withCredentials: true });
        onReactionChange({ type: res.data.type, skin_tone: res.data.skin_tone });
      }
    } catch (err) {
      toast.error(t('feed.failed_react'));
    }
    setBusy(false);
  };

  const onReactionClick = (r) => {
    // If already active with this type, always remove — no picker
    if (userReaction?.type === r.type) {
      handleReact(r.type);
    } else if (r.hasSkin) {
      setShowPicker(showPicker === r.type ? null : r.type);
    } else {
      handleReact(r.type);
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        {REACTIONS.map((r) => {
          const count = reactions[r.type] || 0;
          const isActive = userReaction?.type === r.type;

          return (
            <button
              key={r.type}
              onClick={() => onReactionClick(r)}
              disabled={busy}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all ${
                isActive ? 'ring-1' : 'hover:bg-[var(--theme-hover)]'
              }`}
              style={isActive ? { background: 'var(--glass-bg-active)', ringColor: 'var(--theme-accent)' } : {}}
              data-testid={`react-${r.type}-${postId}`}
            >
              <span className="text-sm">{isActive && userReaction.skin_tone !== 'default' ? getReactionEmoji(r.type, userReaction.skin_tone) : r.base}</span>
              {count > 0 && <span style={{ color: 'var(--theme-textSecondary)' }}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Skin Tone Picker */}
      {showPicker && (
        <div
          className="absolute bottom-full left-0 mb-1 flex items-center gap-0.5 p-1.5 rounded-xl z-50"
          style={{ background: 'var(--theme-surface)', border: '1px solid var(--glass-border)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
          data-testid={`skin-tone-picker-${postId}`}
        >
          {SKIN_TONES.map((tone) => (
            <button
              key={tone.key}
              onClick={() => handleReact(showPicker, tone.key)}
              className="p-1 rounded-lg hover:bg-[var(--theme-hover)] transition-all"
              title={tone.label}
              data-testid={`skin-${tone.key}-${postId}`}
            >
              <span className="text-lg">{getReactionEmoji(showPicker, tone.key)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Comment Reaction Bar (compact) ──────────────────────────────────────────

function CommentReactionBar({ commentId, reactions, userReaction, onReactionChange }) {
  const { t } = useTranslation();
  const [showPicker, setShowPicker] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleReact = async (type, skinTone = 'default') => {
    setBusy(true);
    setShowPicker(null);
    try {
      if (userReaction && userReaction.type === type) {
        await axios.delete(`${API}/feed/comments/${commentId}/react`, { withCredentials: true });
        onReactionChange(commentId, null);
      } else {
        const res = await axios.post(`${API}/feed/comments/${commentId}/react`, { type, skin_tone: skinTone }, { withCredentials: true });
        onReactionChange(commentId, { type: res.data.type, skin_tone: res.data.skin_tone });
      }
    } catch {
      toast.error(t('feed.failed_react'));
    }
    setBusy(false);
  };

  const onReactionClick = (r) => {
    if (userReaction?.type === r.type) {
      handleReact(r.type);
    } else if (r.hasSkin) {
      setShowPicker(showPicker === r.type ? null : r.type);
    } else {
      handleReact(r.type);
    }
  };

  const totalReactions = Object.values(reactions || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="relative inline-flex">
      <div className="flex items-center gap-0.5">
        {REACTIONS.map((r) => {
          const count = (reactions || {})[r.type] || 0;
          const isActive = userReaction?.type === r.type;

          if (!isActive && count === 0) {
            return (
              <button
                key={r.type}
                onClick={() => onReactionClick(r)}
                disabled={busy}
                className="p-0.5 rounded-full text-[10px] opacity-40 hover:opacity-100 transition-opacity"
                data-testid={`comment-react-${r.type}-${commentId}`}
              >
                <span className="text-xs">{r.base}</span>
              </button>
            );
          }

          return (
            <button
              key={r.type}
              onClick={() => onReactionClick(r)}
              disabled={busy}
              className={`flex items-center gap-0.5 px-1 py-0.5 rounded-full text-[10px] transition-all ${
                isActive ? 'ring-1' : 'hover:bg-[var(--theme-hover)]'
              }`}
              style={isActive ? { background: 'var(--glass-bg-active)', ringColor: 'var(--theme-accent)' } : {}}
              data-testid={`comment-react-${r.type}-${commentId}`}
            >
              <span className="text-xs">{isActive && userReaction.skin_tone !== 'default' ? getReactionEmoji(r.type, userReaction.skin_tone) : r.base}</span>
              {count > 0 && <span style={{ color: 'var(--theme-textSecondary)', fontSize: '9px' }}>{count}</span>}
            </button>
          );
        })}
      </div>

      {showPicker && (
        <div
          className="absolute bottom-full left-0 mb-1 flex items-center gap-0.5 p-1 rounded-xl z-50"
          style={{ background: 'var(--theme-surface)', border: '1px solid var(--glass-border)', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
          data-testid={`comment-skin-picker-${commentId}`}
        >
          {SKIN_TONES.map((tone) => (
            <button
              key={tone.key}
              onClick={() => handleReact(showPicker, tone.key)}
              className="p-0.5 rounded-lg hover:bg-[var(--theme-hover)] transition-all"
              title={tone.label}
            >
              <span className="text-sm">{getReactionEmoji(showPicker, tone.key)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Comment ─────────────────────────────────────────────────────────────────

function Comment({ comment, postId, currentUserId, onDelete, onReply, onCommentReaction, depth = 0 }) {
  const { t } = useTranslation();
  const a = comment.author || {};
  const initial = (a.name || a.username || '?')[0].toUpperCase();
  const [showReport, setShowReport] = useState(false);
  const navigate = useNavigate();

  const handleAuthorClick = () => {
    if (comment.user_id && comment.user_id !== currentUserId) {
      navigate(`/community/user/${comment.user_id}`);
    }
  };

  return (
    <div style={{ marginLeft: depth > 0 ? '1.5rem' : 0 }} data-testid={`comment-${comment.comment_id}`}>
      <div className="flex gap-2 py-2">
        <button onClick={handleAuthorClick} className="shrink-0 mt-0.5" data-testid={`comment-author-avatar-${comment.comment_id}`}>
          {a.picture ? (
            <img src={a.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
          ) : (
            <div
              className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold"
              style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
            >
              {initial}
            </div>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleAuthorClick}
              className="text-xs font-medium hover:underline"
              style={{ color: 'var(--theme-text)' }}
              data-testid={`comment-author-name-${comment.comment_id}`}
            >
              {a.name || t('feed.unknown')}
            </button>
            <span className="text-[10px]" style={{ color: 'var(--theme-textSecondary)' }}>
              {new Date(comment.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          </div>
          <p className="text-sm mt-0.5" style={{ color: 'var(--theme-text)' }}>{comment.content}</p>
          <div className="flex items-center gap-2 mt-1">
            <CommentReactionBar
              commentId={comment.comment_id}
              reactions={comment.reactions || {}}
              userReaction={comment.user_reaction}
              onReactionChange={onCommentReaction}
            />
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {depth === 0 && (
              <button
                onClick={() => onReply(comment.comment_id)}
                className="text-[10px] font-medium hover:underline"
                style={{ color: 'var(--theme-accent)' }}
                data-testid={`reply-comment-${comment.comment_id}`}
              >
                {t('generated.communityfeed_reply_d949')}
              </button>
            )}
            {comment.user_id === currentUserId ? (
              <button
                onClick={() => onDelete(comment.comment_id)}
                className="text-[10px] font-medium hover:underline text-destructive"
                data-testid={`delete-comment-${comment.comment_id}`}
              >
                {t('generated.communityfeed_delete_8e39')}
              </button>
            ) : (
              <button
                onClick={() => setShowReport(true)}
                className="text-[10px] font-medium hover:underline"
                style={{ color: 'var(--theme-textSecondary)' }}
                data-testid={`report-comment-${comment.comment_id}`}
              >
                {t('generated.communityfeed_report_cd44')}
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Replies */}
      {comment.replies?.map((r) => (
        <Comment key={r.comment_id} comment={r} postId={postId} currentUserId={currentUserId} onDelete={onDelete} onReply={onReply} onCommentReaction={onCommentReaction} depth={1} />
      ))}
      {showReport && (
        <ReportModal open={showReport} onClose={() => setShowReport(false)} targetType="comment" targetId={comment.comment_id} />
      )}
    </div>
  );
}

// ── Comments Section ────────────────────────────────────────────────────────

function CommentsSection({ postId, commentCount: initialCount, currentUserId }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [count, setCount] = useState(initialCount);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/feed/posts/${postId}/comments`, { withCredentials: true });
      setComments(res.data.comments || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [postId]);

  const handleExpand = () => {
    if (!expanded) fetchComments();
    setExpanded(!expanded);
  };

  const handleSend = async () => {
    const content = text.trim();
    if (!content) return;
    setSending(true);
    try {
      await axios.post(`${API}/feed/posts/${postId}/comments`, {
        content,
        parent_comment_id: replyTo,
      }, { withCredentials: true });
      setText('');
      setReplyTo(null);
      setCount((c) => c + 1);
      await fetchComments();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('feed.failed_comment'));
    }
    setSending(false);
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/feed/comments/${commentId}`, { withCredentials: true });
      toast.success(t('feed.comment_deleted'));
      setCount((c) => Math.max(0, c - 1));
      await fetchComments();
    } catch (err) {
      toast.error(t('feed.failed_delete'));
    }
  };

  const handleCommentReaction = (commentId, reaction) => {
    // Update local state in-place for instant feedback
    const updateReaction = (list) =>
      list.map((c) => {
        if (c.comment_id === commentId) {
          const counts = { ...(c.reactions || {}) };
          // Remove old reaction count
          if (c.user_reaction) {
            const oldType = c.user_reaction.type;
            if (counts[oldType]) counts[oldType] = Math.max(0, counts[oldType] - 1);
            if (counts[oldType] === 0) delete counts[oldType];
          }
          // Add new reaction count
          if (reaction) {
            counts[reaction.type] = (counts[reaction.type] || 0) + 1;
          }
          return { ...c, reactions: counts, user_reaction: reaction };
        }
        if (c.replies) {
          return { ...c, replies: updateReaction(c.replies) };
        }
        return c;
      });
    setComments((prev) => updateReaction(prev));
  };

  return (
    <div>
      <button
        onClick={handleExpand}
        className="flex items-center gap-1.5 text-xs font-medium px-1 py-1"
        style={{ color: 'var(--theme-textSecondary)' }}
        data-testid={`toggle-comments-${postId}`}
      >
        <MessageCircle className="h-3.5 w-3.5" />
        {count} {count === 1 ? 'comment' : 'comments'}
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {expanded && (
        <div className="mt-2">
          {loading ? (
            <div className="flex justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--theme-accent)' }} />
            </div>
          ) : (
            <div className="space-y-0">
              {comments.map((c) => (
                <Comment
                  key={c.comment_id}
                  comment={c}
                  postId={postId}
                  currentUserId={currentUserId}
                  onDelete={handleDelete}
                  onReply={(id) => setReplyTo(id)}
                  onCommentReaction={handleCommentReaction}
                />
              ))}
            </div>
          )}

          {/* Comment input */}
          <div className="mt-2">
            {replyTo && (
              <div className="flex items-center gap-1 text-[10px] mb-1 px-1" style={{ color: 'var(--theme-accent)' }}>
                <Reply className="h-3 w-3" />
                {t('generated.communityfeed_replying_to_comment_7b21')}
                <button onClick={() => setReplyTo(null)}><X className="h-3 w-3" /></button>
              </div>
            )}
            <div
              className="flex items-center gap-2 rounded-xl px-3 py-2"
              style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
            >
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder={t('feed.write_comment')}
                className="flex-1 bg-transparent text-sm outline-none"
                style={{ color: 'var(--theme-text)' }}
                data-testid={`comment-input-${postId}`}
              />
              <button
                onClick={handleSend}
                disabled={sending || !text.trim()}
                className="p-1 rounded-full"
                data-testid={`send-comment-${postId}`}
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--theme-accent)' }} />
                ) : (
                  <Send className="h-4 w-4" style={{ color: text.trim() ? 'var(--theme-accent)' : 'var(--theme-textSecondary)' }} />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Post Card ───────────────────────────────────────────────────────────────


function InlineEiliResponse({ post }) {
  const {
    t
  } = useTranslation();

  const [expanded, setExpanded] = React.useState(false);
  const content = post.eili_response?.content || '';
  const isLong = content.length > 300;

  return (
    <div className="mx-4 mb-3 rounded-xl p-3" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)' }}>
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="h-3.5 w-3.5" style={{ color: 'var(--theme-accent)' }} />
        <span className="text-[11px] font-medium" style={{ color: 'var(--theme-accent)' }}>
          {t('generated.communityfeed_eili_response_2fec')}
        </span>
        {post.eili_response.conversation_title && (
          <span className="text-[10px] ml-1" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('generated.communityfeed_from_0be9')}{post.eili_response.conversation_title}"
                      </span>
        )}
      </div>
      {post.eili_response.question && (
        <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--theme-textSecondary)' }}>
          Q: {post.eili_response.question}
        </p>
      )}
      <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--theme-text)' }}>
        {isLong && !expanded ? content.slice(0, 300) + '...' : content}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs font-medium transition-colors hover:opacity-80"
          style={{ color: 'var(--theme-accent)' }}
          data-testid={`expand-eili-response-${post.post_id}`}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
}


export function PostCard({ post, currentUserId, onDelete, onDiscuss, onAuthorClick }) {
  const { t } = useTranslation();
  const a = post.author || {};
  const initial = (a.name || a.username || '?')[0].toUpperCase();
  const [reactions, setReactions] = useState(post.reactions || {});
  const [userReaction, setUserReaction] = useState(post.user_reaction);
  const [confirming, setConfirming] = useState(false);
  const [discussing, setDiscussing] = useState(false);
  const [showFullResponse, setShowFullResponse] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content || '');
  const [saving, setSaving] = useState(false);
  const [displayContent, setDisplayContent] = useState(post.content || '');
  const [editedAt, setEditedAt] = useState(post.edited_at || null);

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/feed/posts/${post.post_id}`, { content: editContent.trim() }, { withCredentials: true });
      setDisplayContent(editContent.trim());
      setEditedAt(new Date().toISOString());
      setEditing(false);
      toast.success(t('generated.communityfeed_post_updated_385b'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update post');
    } finally {
      setSaving(false);
    }
  };

  const handleReactionChange = (newReaction) => {
    // Recompute counts locally for instant feedback
    const old = userReaction;
    const updated = { ...reactions };

    if (old) {
      updated[old.type] = Math.max(0, (updated[old.type] || 1) - 1);
      if (updated[old.type] === 0) delete updated[old.type];
    }
    if (newReaction) {
      updated[newReaction.type] = (updated[newReaction.type] || 0) + 1;
    }

    setReactions(updated);
    setUserReaction(newReaction);
  };

  const handleDiscuss = async () => {
    setDiscussing(true);
    await onDiscuss(post.post_id);
    setDiscussing(false);
  };

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
      data-testid={`post-card-${post.post_id}`}
    >
      {/* Author Header */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        <button onClick={() => onAuthorClick?.(post.user_id)} className="shrink-0" data-testid={`author-avatar-${post.post_id}`}>
          {a.picture ? (
            <img src={a.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
            >
              {initial}
            </div>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <button onClick={() => onAuthorClick?.(post.user_id)} className="text-left" data-testid={`author-name-${post.post_id}`}>
            <p className="text-sm font-medium truncate hover:underline" style={{ color: 'var(--theme-text)' }}>
              {a.name || 'Unknown'}
            </p>
          </button>
          <p className="text-[11px]" style={{ color: 'var(--theme-textSecondary)' }}>
            {a.username && `@${a.username} · `}
            {new Date(post.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        {/* Edit / Delete (own posts) */}
        {post.user_id === currentUserId && (
          confirming ? (
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full text-destructive hover:bg-destructive/10"
                onClick={() => { onDelete(post.post_id); setConfirming(false); }}
                data-testid={`confirm-delete-post-${post.post_id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full hover:bg-[var(--theme-hover)]"
                onClick={() => setConfirming(false)}
              >
                <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
              </Button>
            </div>
          ) : (
            <div className="flex gap-0.5">
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full hover:bg-[var(--theme-hover)]"
                onClick={() => { setEditing(true); setEditContent(displayContent); }}
                data-testid={`edit-post-${post.post_id}`}
              >
                <Pencil className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setConfirming(true)}
                data-testid={`delete-post-${post.post_id}`}
              >
                <Trash2 className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
              </Button>
            </div>
          )
        )}
      </div>
      {/* Post Content */}
      {editing ? (
        <div className="px-4 pb-2">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full text-sm rounded-lg p-3 resize-none outline-none"
            style={{ background: 'var(--theme-hover)', color: 'var(--theme-text)', border: '1px solid var(--theme-border)', minHeight: '80px' }}
            autoFocus
            data-testid={`edit-textarea-${post.post_id}`}
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" size="sm" className="rounded-lg text-xs" onClick={() => setEditing(false)} disabled={saving}>
              {t('generated.communityfeed_cancel_ce17')}
            </Button>
            <Button size="sm" className="rounded-lg text-xs" style={{ background: 'var(--theme-accent)', color: '#fff' }}
              onClick={handleSaveEdit} disabled={saving || !editContent.trim()}
              data-testid={`save-edit-${post.post_id}`}
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              {t('generated.communityfeed_save_7392')}
            </Button>
          </div>
        </div>
      ) : displayContent && (
        <div className="px-4 pb-2">
          <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--theme-text)' }}>
            {displayContent}
          </p>
          {editedAt && (
            <p className="text-[10px] mt-1" style={{ color: 'var(--theme-textSecondary)' }}>(edited)</p>
          )}
        </div>
      )}
      {/* Eili Response — show EITHER as image (tap to pop out) OR as text */}
      {post.image_url ? (
        <>
          {/* Image post — tap to open full response modal */}
          <div className="mx-4 mb-3">
            <button
              onClick={() => setShowFullResponse(true)}
              className="w-full rounded-xl overflow-hidden transition-all hover:opacity-90"
              style={{ border: '1px solid var(--glass-border)' }}
              data-testid={`post-image-${post.post_id}`}
            >
              <img
                src={post.image_url.startsWith('http') ? post.image_url : `${BACKEND_URL}${post.image_url}`}
                alt={t('feed.eili_insight')}
                className="w-full object-contain"
                style={{ maxHeight: 400 }}
              />
            </button>
            <p className="text-[11px] text-center mt-1.5" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.communityfeed_tap_image_to_783e')}
            </p>
          </div>
          <EiliResponseModal
            post={post}
            open={showFullResponse}
            onClose={() => setShowFullResponse(false)}
            onDiscuss={onDiscuss}
          />
        </>
      ) : post.eili_response && (
        /* Text post — show Eili response inline with expand */
        (<InlineEiliResponse post={post} />)
      )}
      {/* Actions */}
      <div className="px-4 pb-2 flex items-center justify-between">
        <ReactionBar
          postId={post.post_id}
          reactions={reactions}
          userReaction={userReaction}
          onReactionChange={handleReactionChange}
        />
        <div className="flex items-center gap-1">
          {post.user_id !== currentUserId && (
            <button
              onClick={() => { setReportTarget({ type: 'post', id: post.post_id }); setShowReport(true); }}
              className="p-1.5 rounded-full transition-all hover:bg-[var(--theme-hover)]"
              title={t('feed.report_post')}
              data-testid={`report-post-${post.post_id}`}
            >
              <Flag className="h-3 w-3" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
          )}
          <button
            onClick={handleDiscuss}
            disabled={discussing}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all hover:bg-[var(--theme-hover)]"
            style={{ color: 'var(--theme-accent)' }}
            data-testid={`ask-eili-post-${post.post_id}`}
          >
            {discussing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {t('generated.communityfeed_ask_eili_abe5')}
          </button>
        </div>
      </div>
      {/* Comments */}
      <div className="px-4 pb-3" style={{ borderTop: '1px solid var(--glass-border)' }}>
        <CommentsSection
          postId={post.post_id}
          commentCount={post.comment_count || 0}
          currentUserId={currentUserId}
        />
      </div>
      {/* Report Modal */}
      {showReport && reportTarget && (
        <ReportModal
          open={showReport}
          onClose={() => setShowReport(false)}
          targetType={reportTarget.type}
          targetId={reportTarget.id}
        />
      )}
    </div>
  );
}

// ── Create Post UI ──────────────────────────────────────────────────────────

function CreatePostSection({ userId, onPostCreated }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [content, setContent] = useState('');
  const [eiliAttach, setEiliAttach] = useState(null);
  const [shareMode, setShareMode] = useState('text'); // 'text' or 'image'
  const [showPicker, setShowPicker] = useState(false);
  const [eiliResponses, setEiliResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [posting, setPosting] = useState(false);
  const imageCardRef = useRef(null);

  const loadResponses = async () => {
    setLoadingResponses(true);
    try {
      const res = await axios.get(`${API}/feed/eili-responses`, { withCredentials: true });
      setEiliResponses(res.data.responses || []);
    } catch { /* ignore */ }
    setLoadingResponses(false);
  };

  const handleAttach = () => {
    setShowPicker(true);
    if (eiliResponses.length === 0) loadResponses();
  };

  const generateImageData = async () => {
    if (!imageCardRef.current) return null;
    try {
      const canvas = await html2canvas(imageCardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  };

  const handlePost = async () => {
    if (!content.trim() && !eiliAttach) return;
    setPosting(true);
    try {
      const payload = { content: content.trim() };
      if (eiliAttach) {
        payload.eili_response = {
          content: eiliAttach.content,
          question: eiliAttach.question || '',
          conversation_title: eiliAttach.conversation_title || '',
        };
      }
      // Generate and include image if mode is 'image'
      if (shareMode === 'image' && eiliAttach) {
        const imageData = await generateImageData();
        if (imageData) {
          payload.image_data = imageData;
        }
      }
      const res = await axios.post(`${API}/feed/posts`, payload, { withCredentials: true });
      toast.success(t('feed.post_created'));
      setContent('');
      setEiliAttach(null);
      setShareMode('text');
      setExpanded(false);
      onPostCreated(res.data);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('feed.failed_post'));
    }
    setPosting(false);
  };

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full rounded-2xl p-4 text-left transition-all hover:scale-[1.005] active:scale-[0.995]"
        style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
        data-testid="create-post-trigger"
      >
        <div className="flex items-center gap-3">
          <div
            className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
            style={{ background: 'var(--glass-bg-active)' }}
          >
            <Plus className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
          </div>
          <span className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('generated.communityfeed_share_something_with_805e')}
          </span>
        </div>
      </button>
    );
  }

  return (
    <div
      className="rounded-2xl p-4"
      style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
      data-testid="create-post-form"
    >
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={t('feed.whats_on_mind')}
        rows={3}
        className="w-full bg-transparent text-sm outline-none resize-none"
        style={{ color: 'var(--theme-text)' }}
        autoFocus
        data-testid="post-content-input"
      />
      {/* Eili Attachment Preview */}
      {eiliAttach && (
        <div className="mt-2 rounded-xl p-3 relative" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)' }}>
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="h-3 w-3" style={{ color: 'var(--theme-accent)' }} />
            <span className="text-[11px] font-medium" style={{ color: 'var(--theme-accent)' }}>{t('generated.communityfeed_eili_response_attached_92d5')}</span>
          </div>
          <p className="text-xs line-clamp-2" style={{ color: 'var(--theme-text)' }}>
            {eiliAttach.content.slice(0, 150)}...
          </p>
          <button
            onClick={() => { setEiliAttach(null); setShareMode('text'); }}
            className="absolute top-2 right-2 p-0.5 rounded-full hover:bg-[var(--theme-hover)]"
          >
            <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>

          {/* Share as: Text or Image */}
          <div className="flex items-center gap-2 mt-2 pt-2" style={{ borderTop: '1px solid var(--glass-border)' }}>
            <span className="text-[10px] font-medium" style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.communityfeed_share_as_a5af')}</span>
            <button
              onClick={() => setShareMode('text')}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-all"
              style={shareMode === 'text' ? { background: 'var(--theme-accent)', color: '#fff' } : { color: 'var(--theme-textSecondary)' }}
              data-testid="create-mode-text"
            >
              <Type className="h-3 w-3" /> {t('generated.communityfeed_text_2eda')}
            </button>
            <button
              onClick={() => setShareMode('image')}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-all"
              style={shareMode === 'image' ? { background: 'var(--theme-accent)', color: '#fff' } : { color: 'var(--theme-textSecondary)' }}
              data-testid="create-mode-image"
            >
              <ImageIcon className="h-3 w-3" /> {t('generated.communityfeed_image_21e7')}
            </button>
          </div>
        </div>
      )}
      {/* Hidden card for image generation (offscreen) */}
      {shareMode === 'image' && eiliAttach && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <div ref={imageCardRef} style={{ width: 440 }}>
            <EiliShareCard text={eiliAttach.content} />
          </div>
        </div>
      )}
      {/* Eili Response Picker */}
      {showPicker && !eiliAttach && (
        <div className="mt-2 rounded-xl max-h-[200px] overflow-y-auto" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)' }}>
          <div className="flex items-center justify-between px-3 pt-2 pb-1">
            <span className="text-xs font-medium" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.communityfeed_select_an_eili_526c')}
            </span>
            <button onClick={() => setShowPicker(false)}>
              <X className="h-3.5 w-3.5" style={{ color: 'var(--theme-textSecondary)' }} />
            </button>
          </div>
          {loadingResponses ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--theme-accent)' }} />
            </div>
          ) : eiliResponses.length === 0 ? (
            <p className="text-center text-xs py-4" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.communityfeed_no_recent_eili_cdfb')}
            </p>
          ) : (
            <div className="p-1">
              {eiliResponses.map((r) => (
                <button
                  key={r.message_id}
                  onClick={() => { setEiliAttach(r); setShowPicker(false); }}
                  className="w-full text-left p-2 rounded-lg hover:bg-[var(--theme-hover)] transition-all"
                  data-testid={`eili-response-${r.message_id}`}
                >
                  <p className="text-[11px] font-medium truncate" style={{ color: 'var(--theme-accent)' }}>
                    {r.conversation_title || t('feed.conversation')}
                  </p>
                  <p className="text-xs line-clamp-2 mt-0.5" style={{ color: 'var(--theme-text)' }}>
                    {r.preview}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex items-center justify-between mt-3 pt-2" style={{ borderTop: '1px solid var(--glass-border)' }}>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost" size="sm"
            className="h-8 rounded-full text-xs px-3 hover:bg-[var(--theme-hover)]"
            onClick={handleAttach}
            data-testid="attach-eili-response"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1" style={{ color: 'var(--theme-accent)' }} />
            <span style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.communityfeed_attach_eili_7e6c')}</span>
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost" size="sm"
            className="h-8 rounded-full text-xs px-3"
            onClick={() => { setExpanded(false); setContent(''); setEiliAttach(null); }}
          >
            {t('generated.communityfeed_cancel_ce17')}
          </Button>
          <Button
            size="sm"
            className="h-8 rounded-full text-xs px-4"
            style={{ background: 'var(--theme-accent)', color: '#fff' }}
            onClick={handlePost}
            disabled={posting || (!content.trim() && !eiliAttach)}
            data-testid="submit-post-btn"
          >
            {posting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Post'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Feed Component ─────────────────────────────────────────────────────

export default function CommunityFeed({ userId, highlightPostId, onHighlightClear }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subTab, setSubTab] = useState('friends');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const highlightRef = useRef(null);

  const fetchPosts = useCallback(async (tab) => {
    setLoading(true);
    try {
      const endpoint = tab === 'mine' ? 'posts/mine' : 'posts/friends';
      const res = await axios.get(`${API}/feed/${endpoint}`, { withCredentials: true });
      setPosts(res.data.posts || []);
    } catch {
      toast.error(t('feed.failed_load'));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPosts(subTab);
  }, [subTab, fetchPosts]);

  // Scroll to highlighted post when it appears
  useEffect(() => {
    if (highlightPostId && !loading && posts.length > 0) {
      // Check if the post is in current tab, if not try the other tab
      const found = posts.find((p) => p.post_id === highlightPostId);
      if (found) {
        setTimeout(() => {
          highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Clear highlight after animation
          setTimeout(() => onHighlightClear?.(), 2500);
        }, 200);
      } else if (subTab === 'friends') {
        // Try 'mine' tab
        setSubTab('mine');
      }
    }
  }, [highlightPostId, loading, posts, subTab]);

  const handlePostCreated = (newPost) => {
    if (subTab === 'mine') {
      setPosts((prev) => [newPost, ...prev]);
    }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/feed/posts/${postId}`, { withCredentials: true });
      toast.success(t('feed.post_deleted'));
      setPosts((prev) => prev.filter((p) => p.post_id !== postId));
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('feed.failed_delete'));
    }
  };

  const handleDiscuss = async (postId) => {
    try {
      const res = await axios.post(`${API}/feed/posts/${postId}/discuss`, {}, { withCredentials: true });
      toast.success(t('feed.conversation_created'));
      navigate(`/chat?c=${res.data.conversation_id}`);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('feed.failed_conversation'));
    }
  };

  return (
    <div data-testid="community-feed-tab">
      {/* Sub-tabs */}
      <div className="flex items-center gap-2 mb-4">
        <SubTab active={subTab === 'friends'} label={t('feed.friends_posts')} onClick={() => setSubTab('friends')} testId="subtab-friends-posts" />
        <SubTab active={subTab === 'mine'} label={t('feed.my_posts')} onClick={() => setSubTab('mine')} testId="subtab-my-posts" />
      </div>

      {/* Create Post */}
      <div className="mb-4">
        <CreatePostSection userId={userId} onPostCreated={handlePostCreated} />
      </div>

      {/* Posts */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <MessageCircle className="h-12 w-12 mx-auto mb-3" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>
            {subTab === 'mine' ? t('feed.no_posts_mine') : t('feed.no_posts_friends')}
          </p>
          <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
            {subTab === 'mine' ? t('feed.no_posts_mine_hint') : t('feed.no_posts_friends_hint')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => (
            <div
              key={p.post_id}
              ref={p.post_id === highlightPostId ? highlightRef : undefined}
              className={`transition-all duration-700 rounded-2xl ${p.post_id === highlightPostId ? 'ring-2 ring-[var(--theme-accent)] ring-offset-2 ring-offset-[var(--theme-bg)]' : ''}`}
            >
              <PostCard
                post={p}
                currentUserId={userId}
                onDelete={handleDelete}
                onDiscuss={handleDiscuss}
                onAuthorClick={(uid) => navigate(`/community/user/${uid}`)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Eili Share Card (rendered offscreen, captured to image via html2canvas) ──
function EiliShareCard({ text }) {
  const {
    t
  } = useTranslation();

  const displayText = text.length > 500 ? text.slice(0, 500) + '...' : text;

  return (
    <div
      style={{
        background: '#1a1a2e',
        padding: '32px 28px 24px',
        fontFamily: "'Roboto', -apple-system, sans-serif",
        minHeight: 200,
        position: 'relative',
      }}
    >
      {/* Accent bar */}
      <div
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 4,
          background: '#c9a44a',
        }}
      />
      {/* Branding */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <img
          src="/logo.png"
          alt={t('generated.communityfeed_eili_62bf')}
          style={{ width: 28, height: 28, objectFit: 'contain' }}
          crossOrigin="anonymous"
        />
        <span
          style={{
            fontFamily: "'Playwrite ES Deco', cursive",
            fontSize: 18,
            fontWeight: 700,
            color: '#c9a44a',
          }}
        >
          {t('generated.communityfeed_eili_62bf')}
        </span>
        <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>
          {t('generated.communityfeed_the_word_first_93a6')}
        </span>
      </div>
      {/* Divider */}
      <div style={{ height: 1, background: '#333', marginBottom: 18 }} />
      {/* Text */}
      <div
        style={{
          fontSize: 14,
          lineHeight: 1.7,
          color: '#e0e0e0',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {displayText}
      </div>
      {/* Footer */}
      <div
        style={{
          marginTop: 20,
          paddingTop: 14,
          borderTop: '1px solid #333',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontSize: 11, color: '#888' }}>
          {t('generated.communityfeed_shared_via_eili_7570')}
        </span>
      </div>
    </div>
  );
}
